'use client';

import {
  Button,
  IcCloseCircle,
  IconButton,
  IcSearch,
  Image,
  ImageCircleItem,
  Typography,
} from '@musinsa/mds';
import { useDeviceInfo } from '@musinsa/packages-device-detection';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { pathsMusinsa } from '../../common';
import * as InfiniteList from '../../common/components/InfiniteList';
import { useNavigate } from '../../common/hooks/useNavigate';
import { ReverseProposalProduct } from '../../product/components/variants/ReverseProposalProduct';
import { buildSelectItemGtmAttr } from '../../product/utils/buildSelectItemGtmAttr';
import { curatorQueries } from '../../query-factory';
import { useReverseProposalBrands } from '../hooks/useReverseProposalBrands';
import { useReverseProposalGoods } from '../hooks/useReverseProposalGoods';
import {
  isMusinsaService,
  trackCampaignSearchBrandGoodsClickMusinsa,
  trackCampaignSearchGoodsSelectTryClickMusinsa,
  trackCampaignSearchKeywordClickMusinsa,
  trackCampaignSearchScreenViewMusinsa,
} from '../utils/campaignProposalMusinsaAnalytics';
import { EmptyState } from './common/EmptyState';
import { DropDownMenu } from './DropDownMenu';

export const SearchPageView = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { service } = useDeviceInfo();
  const brandId = searchParams.get('brandId') ?? undefined;
  const brandName = searchParams.get('brandName') ?? undefined;
  const urlKeyword = searchParams.get('keyword') ?? '';

  const [keyword, setKeyword] = useState(urlKeyword);
  const { data: userInfo } = useQuery({
    ...curatorQueries.profile.userInfo,
  });
  const snsTitle = userInfo?.snsTitle ?? '';
  const { data: profile } = useQuery({
    ...curatorQueries.profile.profileBySnsTitle(snsTitle),
    enabled: !!snsTitle,
  });
  const nickname = profile?.nickname ?? '';

  const selectedBrand = brandId && brandName ? { id: brandId, name: brandName } : undefined;

  const handleSelectBrand = (id: string, name: string) => {
    router.push(pathsMusinsa.campaignSearch({ query: { brandId: id, brandName: name } }));
  };

  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();

  const updateKeywordUrl = useCallback(
    (value: string) => {
      const selectedBrandQuery = brandId && brandName ? { brandId, brandName } : {};

      router.replace(
        pathsMusinsa.campaignSearch({
          query: {
            ...selectedBrandQuery,
            keyword: value || undefined,
          },
        }),
      );
    },
    [brandId, brandName, router],
  );

  const handleKeywordChange = useCallback(
    (value: string) => {
      setKeyword(value);
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        updateKeywordUrl(value);
        if (isMusinsaService(service)) {
          trackCampaignSearchKeywordClickMusinsa({ keyword: value });
        }
      }, 400);
    },
    [service, updateKeywordUrl],
  );

  const handleClickRemove = () => {
    setKeyword('');
    updateKeywordUrl('');
  };

  const handleClickSearch = () => {
    if (!keyword?.length) return;
    updateKeywordUrl(keyword);
  };

  useEffect(() => {
    return () => clearTimeout(debounceTimer.current);
  }, []);

  useEffect(() => {
    if (!isMusinsaService(service)) return;
    trackCampaignSearchScreenViewMusinsa();
  }, [service]);

  const showSearch = urlKeyword.length > 0 || selectedBrand !== undefined;

  return (
    <div className="flex flex-col flex-1 bg-gray-100">
      {/* 검색 입력 — 레거시 SuggestionSearchInput 동일 */}
      <div className="px-4 pt-3 pb-2">
        <div className="flex justify-between items-center gap-2 relative w-full h-9 bg-white border border-gray-300 rounded-[4px] px-2 py-1.5">
          {selectedBrand && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded shrink-0">
              <Typography variant="body13pxMed" colorScheme="gray-600">
                {selectedBrand.name}
              </Typography>
            </span>
          )}
          <input
            type="text"
            value={keyword}
            onChange={(e) => handleKeywordChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleClickSearch();
            }}
            placeholder={selectedBrand ? '브랜드 상품 검색' : '검색어를 입력해 보세요.'}
            className="flex-1 outline-none min-w-0"
          />
          <div className="flex items-center gap-2">
            {keyword.length > 0 && (
              <IconButton onClick={handleClickRemove} aria-label="텍스트 삭제" size="size20x20">
                <IcCloseCircle fillColor="gray-400" />
              </IconButton>
            )}
            <IconButton onClick={handleClickSearch} aria-label="검색" size="size20x20">
              <IcSearch strokeColor="gray-500" />
            </IconButton>
          </div>
        </div>
      </div>

      {/* 브랜드 목록 or 상품 검색 결과 */}
      {showSearch ? (
        <GoodsSearchResult
          keyword={urlKeyword}
          brandId={selectedBrand?.id}
          isMusinsa={isMusinsaService(service)}
          nickname={nickname}
        />
      ) : (
        <BrandList
          onSelectBrand={handleSelectBrand}
          isMusinsa={isMusinsaService(service)}
          nickname={nickname}
        />
      )}
    </div>
  );
};

function BrandList({
  onSelectBrand,
  isMusinsa,
  nickname,
}: {
  onSelectBrand: (brandId: string, brandName: string) => void;
  isMusinsa: boolean;
  nickname: string;
}) {
  const [sortType, setSortType] = useState<'LATEST' | 'BRAND_NAME'>('LATEST');
  const [sortIndex, setSortIndex] = useState(0);
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useReverseProposalBrands({ size: 20, sortType });

  const brands = data?.pages.flatMap((page) => page.list) ?? [];
  const total = data?.pages[0]?.total ?? brands.length;

  if (isLoading) {
    return null;
  }

  if (brands.length === 0) {
    return (
      <div className="flex items-center grow">
        <EmptyState
          title="검색 결과가 없습니다."
          subTitle="브랜드명/상품명을 검색하고 캠페인을 제안해보세요."
        />
      </div>
    );
  }

  const SORT_ITEMS = [
    { value: 'LATEST', label: '신규 브랜드순' },
    { value: 'BRAND_NAME', label: '브랜드명순' },
  ];

  return (
    <div className="flex flex-col pb-[72px]!">
      {/* 타이틀 — 레거시: Padding top=3 x=4 + title18pxMed */}
      <div className="px-4 pt-3">
        <Typography variant="title18pxMed">제안 가능한 브랜드</Typography>
      </div>

      {/* 카운트 + 정렬 */}
      <div className="flex items-center justify-between gap-5 px-4 py-3">
        <Typography variant="body13pxReg" colorScheme="gray-600">
          {total}개 브랜드
        </Typography>
        <DropDownMenu
          items={SORT_ITEMS}
          selectedIndex={sortIndex}
          onSelect={(value, index) => {
            setSortType(value as 'LATEST' | 'BRAND_NAME');
            setSortIndex(index);
          }}
        />
      </div>

      {/* 브랜드 리스트 */}
      <InfiniteList.Root
        loadMore={() => fetchNextPage()}
        isPending={isFetchingNextPage}
        hasMore={hasNextPage}
      >
        {brands.map((brand) => (
          <li key={brand.brand} className="flex items-center gap-2 px-4 py-3 list-none">
            <ImageCircleItem size="32" type="logo" className="flex-shrink-0">
              <Image src={brand.logoImage} alt={brand.brandName} />
            </ImageCircleItem>
            <Typography variant="body14pxMed" colorScheme="black" className="flex-1 truncate">
              {brand.brandName}
            </Typography>
            <Button
              variant="tertiaryOutlinedGray"
              size={24}
              style={{ flexShrink: 0 }}
              onClick={() => {
                if (isMusinsa) {
                  trackCampaignSearchBrandGoodsClickMusinsa({
                    brandId: brand.brand,
                    id: nickname,
                  });
                }
                onSelectBrand(brand.brand, brand.brandName);
              }}
            >
              <Typography variant="body13pxMed">상품보기</Typography>
            </Button>
          </li>
        ))}
        <InfiniteList.Trigger />
      </InfiniteList.Root>
    </div>
  );
}

function GoodsSearchResult({
  keyword,
  brandId,
  isMusinsa,
  nickname,
}: {
  keyword: string;
  brandId?: string;
  isMusinsa: boolean;
  nickname: string;
}) {
  const { navigate } = useNavigate();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useReverseProposalGoods({
      keyword: keyword || undefined,
      brandId,
      size: 30,
    });

  const goods = data?.pages.flatMap((page) => page.list) ?? [];

  if (isLoading) {
    return null;
  }

  if (goods.length === 0) {
    return (
      <div className="flex items-center py-[40px]!">
        <EmptyState
          title="검색 결과가 없습니다."
          subTitle="브랜드명/상품명을 검색하고 협업을 제안해보세요."
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="px-4 py-3">
        <Typography variant="body13pxReg" colorScheme="gray-600">
          {(data?.pages[0]?.total ?? goods.length).toLocaleString()}개
        </Typography>
      </div>
      <InfiniteList.Root
        className="pb-[72px]!"
        loadMore={() => fetchNextPage()}
        isPending={isFetchingNextPage}
        hasMore={hasNextPage}
      >
        <li className="grid grid-cols-3 list-none">
          {goods.map((item, index) => {
            const { className: gtmClassName, ...gtmDataAttrs } = isMusinsa
              ? buildSelectItemGtmAttr({ ...item, normalPrice: item.price }, index)
              : { className: undefined };

            return (
              <ReverseProposalProduct
                key={item.goodsNo}
                item={item}
                index={index}
                className={twMerge('h-full', gtmClassName)}
                callbackClick={() =>
                  navigate(`${process.env.NEXT_PUBLIC_MUSINSA_BASE_URL}/app/goods/${item.goodsNo}`)
                }
                {...gtmDataAttrs}
              >
                <div className="px-3 pb-6">
                  <Link
                    href={pathsMusinsa.campaignSearchSuggestion({
                      query: { goodsNo: item.goodsNo },
                    })}
                    onClick={() => {
                      if (!isMusinsa) return;
                      trackCampaignSearchGoodsSelectTryClickMusinsa({ id: nickname });
                    }}
                  >
                    <Button variant="tertiaryOutlinedGray" className="w-full h-6" size={24}>
                      <Typography variant="body13pxMed">제안하기</Typography>
                    </Button>
                  </Link>
                </div>
              </ReverseProposalProduct>
            );
          })}
        </li>
        <InfiniteList.Trigger />
      </InfiniteList.Root>
    </div>
  );
}
